import { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Image } from "react-bootstrap";
import queryBlogEntry from "services/queryBlogEntry";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useTimeOnPageContext } from "contexts/time-on-page";
import { AmplitudePageName } from "utils/amplitude";
import { Helmet } from "react-helmet";
import BlogCard from "../blog-card/BlogCard";
import CategorySwitch from "../category-switch/CategorySwitch";
import SubscribeToNewsletter from "components/subscribe-to-newsletter/SubscribeToNewsletter";
import ErrorMessage from "components/error-message";
import Footer from "components/footer/Footer";

import getClasses from "utils/getClasses";
import styles from "./BlogList.module.scss";
import {
  ALL_POSTS_BLOG_CATEGORY,
  BlogCategory,
} from "components/Blogging/constants/blog-categories";
import TEXT_CONTENT_BY_CATEGORY from "./text-content-by-category";

const BLOG_LANDING = "/img/blog/blog-landing.png";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "blog-landing-page";

interface BlogListProps {
  initialCategory?: BlogCategory;
}

const BlogList = ({ initialCategory }: BlogListProps) => {
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [currentCategory, setCurrentCategory] = useState(
    initialCategory || ALL_POSTS_BLOG_CATEGORY,
  );

  const { isPhonePortrait } = usePhonePortraitContext();

  const { setPage } = useTimeOnPageContext();

  useEffect(() => {
    setCurrentCategory(initialCategory || ALL_POSTS_BLOG_CATEGORY);
  }, [initialCategory]);

  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  useEffect(() => {
    queryBlogEntry()
      .then((res) => {
        if (currentCategory !== ALL_POSTS_BLOG_CATEGORY) {
          const blogsInCurrentCategory = res.filter((blog) =>
            blog.tags?.includes(currentCategory.label),
          );
          setBlogs(blogsInCurrentCategory);
        } else {
          setBlogs(res || []);
        }
      })
      .catch(console.error);
  }, [currentCategory]);

  const blogsSorted = useMemo(
    () =>
      [...blogs].sort((a, b) =>
        a.create_datetime < b.create_datetime ? 1 : -1,
      ),
    [blogs],
  );

  const [error, setError] = useState<Error | undefined>(undefined);

  const { metaTitle, metaDescription, displayTitle, displayDescription } =
    useMemo(
      () => TEXT_CONTENT_BY_CATEGORY[currentCategory.value],
      [currentCategory],
    );

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />

        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta
          property="og:image"
          content="https://orchid.exchange/img/blog/blog-list-meta.png"
        />
        <meta
          property="og:image:url"
          content="https://orchid.exchange/img/blog/blog-list-meta.png"
        />
        <meta
          property="og:image:secure_url"
          content="https://orchid.exchange/img/blog/blog-list-meta.png"
        />
        <meta
          property="og:image:alt"
          content="Four mental health professionals engaged in a discussion, representing mental healthcare discourse."
        />
        <meta property="og:image:width" content="4800" />
        <meta property="og:image:height" content="2520" />
        <meta property="og:image:type" content="image/png" />

        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta
          name="twitter:image"
          content="https://orchid.exchange/img/blog/blog-list-meta.png"
        />
        <meta
          name="twitter:image:src"
          content="https://orchid.exchange/img/blog/blog-list-meta.png"
        />
        <meta
          name="twitter:image:alt"
          content="Four mental health professionals engaged in a discussion, representing mental healthcare discourse."
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
        />
      )}

      <div className={styles["header-background"]}>
        <Container
          className={
            !isPhonePortrait
              ? styles["blog-posts__header-container"]
              : styles["blog-posts__header-container--phone"]
          }
        >
          <Row
            className={getClasses(
              "align-items-center",
              !isPhonePortrait &&
                styles["blog-posts__header-container__content--desktop"],
            )}
          >
            <Col className="p-0" md={1} lg={1} />
            <Col className="p-0" md={4} lg={4}>
              <h1
                className={getClasses(
                  styles["blog-posts__header"],
                  isPhonePortrait && styles["blog-posts__header--phone"],
                )}
              >
                {displayTitle}
              </h1>
              <p
                className={getClasses(
                  styles["blog-posts__sub-header"],
                  isPhonePortrait && styles["blog-posts__sub-header--phone"],
                )}
              >
                {displayDescription}
              </p>
            </Col>
            <Col
              md={6}
              lg={6}
              className={`p-0 ${
                isPhonePortrait ? "text-center" : "text-right"
              }`}
            >
              <Image
                alt={
                  "Four mental health professionals engaged in a discussion, representing mental healthcare discourse."
                }
                src={BLOG_LANDING}
                className={getClasses(
                  styles["blog-posts__landing-img"],
                  isPhonePortrait && styles["blog-posts__landing-img--phone"],
                )}
                fluid
              />
            </Col>
          </Row>
        </Container>
      </div>
      <Row
        className={
          !isPhonePortrait
            ? styles["blog-posts__category-switch-wrapper"]
            : styles["blog-posts__category-switch-wrapper--phone"]
        }
      >
        <CategorySwitch
          amplitudePageName={AMPLITUDE_PAGE_NAME}
          currentCategory={currentCategory}
          setCurrentCategory={setCurrentCategory}
        />
      </Row>
      <div
        className={
          !isPhonePortrait
            ? styles["blog-posts__main-container"]
            : styles["blog-posts__main-container--phone"]
        }
      >
        <Row
          className={getClasses(
            styles["blog-posts__list"],
            isPhonePortrait && styles["blog-posts__list--phone"],
          )}
        >
          {blogsSorted.map((blog) => (
            <Col
              xs={12}
              md={4}
              lg={4}
              className={getClasses(
                styles["blog-post"],
                isPhonePortrait && styles["blog-post--phone"],
              )}
              key={blog.id}
            >
              <BlogCard blog={blog} amplitudePageName={AMPLITUDE_PAGE_NAME} />
            </Col>
          ))}
        </Row>
      </div>
      <SubscribeToNewsletter
        setError={setError}
        AMPLITUDE_PAGE_NAME={AMPLITUDE_PAGE_NAME}
      />
      <Footer AMPLITUDE_PAGE_NAME={AMPLITUDE_PAGE_NAME} />
    </>
  );
};

export default BlogList;
